import { useTranslation } from 'react-i18next';
import { FileText, DollarSign, Receipt, AlertTriangle, Loader2, Users } from 'lucide-react';
import { FormModal, MODAL_COLORS } from '@/components/modal';
import { useBulkUserRelatedRecords, useDeleteUser } from '../hooks';
import toast from 'react-hot-toast';

interface BulkDeleteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  userIds: string[];
  onDeleted: () => void;
}

export function BulkDeleteUsersModal({ isOpen, onClose, userIds, onDeleted }: BulkDeleteUsersModalProps) {
  const { t } = useTranslation();
  const { totals, isLoading } = useBulkUserRelatedRecords(userIds, isOpen);
  const deleteMutation = useDeleteUser();

  const hasRecords = totals && (totals.reports > 0 || totals.payrollEntries > 0 || totals.payrollRuns > 0);

  const handleSubmit = async () => {
    await Promise.all(userIds.map((id) => deleteMutation.mutateAsync(id)));
    toast.success(t('users.bulkDeletedSuccess'));
    onClose();
    onDeleted();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('users.bulkDeleteTitle')}
      onSubmit={isLoading ? undefined : handleSubmit}
      isPending={deleteMutation.isPending}
      submitLabel={hasRecords ? t('users.deleteConfirmModal.confirmWithRecords') : t('users.deleteConfirmModal.confirmClean')}
      maxWidth="max-w-md"
    >
      <div className="flex flex-col gap-3">
        {/* User count card */}
        <div
          className="flex items-center gap-3 p-3 rounded-xl"
          style={{
            backgroundColor: MODAL_COLORS.interactive,
            border: `1px solid ${MODAL_COLORS.border}`,
          }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(185, 28, 28, 0.1)' }}
          >
            <Users className="w-5 h-5" style={{ color: '#B91C1C' }} />
          </div>
          <span className="font-bold text-sm" style={{ color: MODAL_COLORS.text }}>
            {t('users.bulkDeleteMessage', { count: userIds.length })}
          </span>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: MODAL_COLORS.text }} />
          </div>
        )}

        {/* Related records warning */}
        {!isLoading && hasRecords && (
          <>
            <div
              className="flex items-start gap-2.5 p-3 rounded-xl"
              style={{
                backgroundColor: 'rgba(254, 226, 226, 0.6)',
                border: '1px solid rgba(185, 28, 28, 0.15)',
              }}
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#B91C1C' }} />
              <span className="text-sm font-medium" style={{ color: '#B91C1C' }}>
                {t('users.deleteConfirmModal.warning')}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {totals.reports > 0 && (
                <RecordRow icon={FileText} label={t('users.deleteConfirmModal.reports')} count={totals.reports} />
              )}
              {totals.payrollEntries > 0 && (
                <RecordRow icon={DollarSign} label={t('users.deleteConfirmModal.payrollEntries')} count={totals.payrollEntries} />
              )}
              {totals.payrollRuns > 0 && (
                <RecordRow icon={Receipt} label={t('users.deleteConfirmModal.payrollRuns')} count={totals.payrollRuns} />
              )}
            </div>
          </>
        )}

        {/* No related records */}
        {!isLoading && !hasRecords && totals && (
          <p className="text-sm text-center py-2 opacity-70">
            {t('users.deleteConfirmModal.noRelatedRecords')}
          </p>
        )}
      </div>
    </FormModal>
  );
}

function RecordRow({ icon: Icon, label, count }: { icon: typeof FileText; label: string; count: number }) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2 rounded-lg"
      style={{
        backgroundColor: MODAL_COLORS.interactive,
        border: `1px solid ${MODAL_COLORS.border}`,
      }}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: MODAL_COLORS.text, opacity: 0.6 }} />
        <span className="text-sm" style={{ color: MODAL_COLORS.text }}>{label}</span>
      </div>
      <span className="text-sm font-bold" style={{ color: '#B91C1C' }}>{count}</span>
    </div>
  );
}
